import React, { useState, useEffect } from 'react';
import { Box, Button, Paper, TextField, Typography, Alert, FormControlLabel, Checkbox } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import SingleSelect from '../../components/formItems/SingleSelect';
import unifiedApi from '../../utils/unifiedApi';

const GAME_TYPE_OPTIONS = [
  { id: 'Regular', name: 'Regular' },
  { id: 'Playoff', name: 'Playoff' },
  { id: 'Friendly', name: 'Friendly' },
  { id: 'SemiFinal', name: 'Semi-Final' },
  { id: 'Final', name: 'Final' },
  { id: 'Bye', name: 'BYE' },
];

const GameForm = () => {
  const { orgId, leagueId, gameId } = useParams();
  const navigate = useNavigate();

  // State for derived league ID when not provided in URL
  const [derivedLeagueId, setDerivedLeagueId] = useState(null);

  // ⚠️ Use Day.js objects, not strings
  // Default start: next Thursday at 8:30pm
  // Default end: next Thursday at 9:30pm
  const getNextThursday = () => {
    const today = dayjs();
    // 4 = Thursday (dayjs: Sunday=0)
    const daysUntilThursday = (4 - today.day() + 7) % 7 || 7;
    return today.add(daysUntilThursday, 'day');
  };
  const nextThursday = getNextThursday();
  const [start, setStart] = useState(nextThursday.hour(20).minute(30).second(0).millisecond(0));
  const [end, setEnd] = useState(nextThursday.hour(21).minute(30).second(0).millisecond(0));
  const [description, setDescription] = useState('Game');
  const [teams, setTeams] = useState([]);
  const [venues, setVenues] = useState([]);
  const [fields, setFields] = useState([]);
  const [dateError, setDateError] = useState('');
  const [apiError, setApiError] = useState('');
  const [isLoadingGame, setIsLoadingGame] = useState(false);

  const [homeTeamId, setHomeTeamId] = useState('');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [gameType, setGameType] = useState('Regular');
  const [isPublished, setIsPublished] = useState(true);
  const isByeGame = gameType === 'Bye';

  // Fetch game data if editing
  // Store fieldId to set after fields are loaded
  const [pendingFieldId, setPendingFieldId] = useState('');
  useEffect(() => {
    if (gameId) {
      setIsLoadingGame(true);
      unifiedApi.get(`/api/Games/${gameId}`)
        .then((game) => {
          if (!game) return;
          setStart(dayjs(game.start));
          setEnd(dayjs(game.end));
          setDescription(game.description ?? '');
          setHomeTeamId(game.homeTeamId?.toString() ?? '');
          setAwayTeamId(game.awayTeamId?.toString() ?? '');
          setVenueId(game.venueId?.toString() ?? '');
          setPendingFieldId(game.fieldId?.toString() ?? '');
          setGameType(game.gameType ?? 'Regular');
          setIsPublished(game.isPublished ?? true);
          
          if (!leagueId && game.leagueId) {
            setDerivedLeagueId(game.leagueId.toString());
          }
        })
        .catch(() => {})
        .finally(() => {
          setIsLoadingGame(false);
        });
    }
  }, [gameId, leagueId]);

  useEffect(() => {
    const effectiveLeagueId = leagueId || derivedLeagueId;
    
    if (effectiveLeagueId) {
      unifiedApi.get(`/api/teams?leagueId=${effectiveLeagueId}`)
        .then((data) => setTeams(data || []))
        .catch(() => setTeams([]));
    }
    
    unifiedApi.getVenues()
      .then((data) => setVenues(data || []))
      .catch(() => setVenues([]));

    setFields([]);
    if (venueId) {
      unifiedApi.get(`/api/Fields?venueId=${venueId}`)
        .then((data) => setFields(data || []))
        .catch(() => setFields([]));
    }
  }, [leagueId, derivedLeagueId, venueId]);

  // Set fieldId after fields are loaded and pendingFieldId is set
  useEffect(() => {
    if (pendingFieldId && fields.length > 0) {
      if (fields.some(f => f.id?.toString() === pendingFieldId)) {
        setFieldId(pendingFieldId);
      }
      setPendingFieldId('');
    }
  }, [fields, pendingFieldId]);

  useEffect(() => {
    if (isByeGame) {
      setAwayTeamId('');
      setVenueId('');
      setFieldId('');
      setPendingFieldId('');
    }
  }, [isByeGame]);

  // Validate dates and set error message
  useEffect(() => {
    if (start && end) {
      if (end.isBefore(start) || end.isSame(start)) {
        setDateError('End time must be after start time');
      } else {
        setDateError('');
      }
    }
  }, [start, end]);

  const handleStartChange = (newStart) => {
    if (newStart) {
      setStart(newStart);
      // Auto-update end time to 1 hour later, but only if not loading game data
      if (!isLoadingGame) {
        setEnd(newStart.add(1, 'hour'));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    
    // Prevent submission if there's a date error
    if (dateError) {
      return;
    }

    if (gameType !== 'Friendly' && !isByeGame && !fieldId) {
      setApiError('Field is required unless the game type is Friendly or BYE.');
      return;
    }
    
    const isEdit = !!gameId;
    const effectiveLeagueId = leagueId || derivedLeagueId;
    const endpoint = isEdit ? `/api/Games/${gameId}` : '/api/Games';
    const selectedAwayTeamId = isByeGame ? null : Number(awayTeamId);
    const selectedVenueId = isByeGame ? null : Number(venueId);
    const selectedFieldId = isByeGame || !fieldId ? null : Number(fieldId);
    const body = isEdit
      ? {
          Id: Number(gameId),
          // Send wall-clock time (no timezone suffix) so the backend interprets
          // it as tenant-local time before converting to UTC for storage.
          start: start.format('YYYY-MM-DDTHH:mm:ss'),
          end: end.format('YYYY-MM-DDTHH:mm:ss'),
          description,
          leagueId: Number(effectiveLeagueId),
          homeTeamId: Number(homeTeamId),
          awayTeamId: selectedAwayTeamId,
          venueId: selectedVenueId,
          fieldId: selectedFieldId,
          gameType,
          isPublished,
        }
      : {
          start: start.format('YYYY-MM-DDTHH:mm:ss'),
          end: end.format('YYYY-MM-DDTHH:mm:ss'),
          description,
          leagueId: Number(effectiveLeagueId),
          homeTeamId: Number(homeTeamId),
          awayTeamId: selectedAwayTeamId,
          venueId: selectedVenueId,
          fieldId: selectedFieldId,
          gameType,
          isPublished,
        };
    try {
      if (isEdit) {
        await unifiedApi.put(endpoint, body);
      } else {
        await unifiedApi.post(endpoint, body);
      }
    } catch (error) {
      setApiError(error.message || 'An error occurred while saving the game.');
      return;
    }
    
    // Navigate back to league schedule, using orgId/leagueId if available, 
    // otherwise go back in history for simplified routes
    if (orgId && effectiveLeagueId) {
      navigate(`/organization/${orgId}/league/${effectiveLeagueId}`);
    } else {
      navigate(-1); // Go back to previous page
    }
  };

  return (
    <>
      <Box p={2}>
        <Paper elevation={3} sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {gameId ? 'Edit Game' : 'Add New Game'}
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Start"
                value={start}
                onChange={handleStartChange}
                renderInput={(params) => <TextField {...params} required fullWidth />}
              />
              <DateTimePicker
                label="End"
                value={end}
                onChange={(v) => setEnd(v ?? dayjs())}
                renderInput={(params) => <TextField {...params} required fullWidth />}
              />
            </LocalizationProvider>

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />

            <SingleSelect
              label="Home Team"
              items={teams}
              value={homeTeamId}
              onChange={(e) => {
                setHomeTeamId(e.target.value);
                if (e.target.value === awayTeamId) setAwayTeamId('');
              }}
            />

            <SingleSelect
              label="Game Type"
              labelId="game-type-select-label"
              items={GAME_TYPE_OPTIONS}
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
            />

            {!isByeGame && (
              <>
                <SingleSelect
                  label="Away Team"
                  items={teams.filter((t) => t.id.toString() !== homeTeamId)}
                  value={awayTeamId}
                  onChange={(e) => setAwayTeamId(e.target.value)}
                />

                <SingleSelect
                  label="Venue"
                  items={venues}
                  value={venueId}
                  onChange={(e) => {
                    setVenueId(e.target.value);
                    setFieldId('');
                  }}
                />

                <SingleSelect
                  label="Field"
                  items={fields}
                  value={fieldId}
                  onChange={(e) => setFieldId(e.target.value)}
                  disabled={!venueId}
                />
              </>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  color="primary"
                />
              }
              label="Published (visible on public schedule)"
              sx={{ mt: 2, mb: 1 }}
            />

            {dateError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {dateError}
              </Alert>
            )}

            {apiError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {apiError}
              </Alert>
            )}

            <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <Button 
                variant="outlined" 
                onClick={() => navigate(-1)}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: { xs: '80px', sm: 'auto' },
                  padding: { xs: '6px 12px', sm: '8px 16px' }
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained"
                disabled={!!dateError}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: { xs: '100px', sm: 'auto' },
                  padding: { xs: '6px 12px', sm: '8px 16px' }
                }}
              >
                {gameId ? 'Update' : 'Create Game'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default GameForm;
